import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import FormAccordion from "../../components/Form/FormAccordion";
import { ButtonGroup, Formgroup, Button } from "../Products/Forms/ProductForm.styled";
import { InputField, SelectFieldStatus } from "../../components/Form/FormComponents";
import FileUpload from "../logistics/partials/FileUpload";
import saveIcon from "../../assets/icons/Edit-Square.svg";
import { getCurrentUserInfo } from "../../utils/UserAuth";
import { useUploadFile } from "../../utils/useUpload";
import { Backdrop, CircularProgress } from "@mui/material";
import { BUSINESSTYPE, createOrUpdateCompanyDetails, stateAndLga } from "./utils";
import { getCurrentDate } from "../lease/utils";
import { saveCompanyInsuranceInfo } from "../../redux/AppSlice";

const OtherAdminDetails = ({ companyData }) => {
	const dispatch = useDispatch();

	const infoToUse = companyData?.companyInsuranceInfo;

	const currentAdmin = getCurrentUserInfo();
	const formattedDate = getCurrentDate();

	const [submitLoading, setSubmitLoading] = useState(false);

	const [formData, setFormData] = useState({
		firstName: infoToUse?.firstName || "",
		lastName: infoToUse?.lastName || "",
		dob: infoToUse?.dob || "",
		email: infoToUse?.email || "",
		phone: infoToUse?.phone || "",
		cac: null,
		utilityBill: null,
		occupation: infoToUse?.occupation || "",
		streetNum: infoToUse?.streetNum || "",
		street: infoToUse?.street || "",
		city: infoToUse?.city || "",
		state: infoToUse?.state || "",
		country: infoToUse?.country || "Nigeria",
		idType: infoToUse?.idType || "CAC Certificate",
		idNumber: infoToUse?.idNumber || "",
		postalCode: infoToUse?.postalCode || "",
		expiryDate: infoToUse?.expiryDate || "",
		cacDoc: infoToUse?.cacDoc || null,
		utilityDoc: infoToUse?.utilityDoc || null,
	});

	const [isEditable, setIsEditable] = useState(false);

	const {
		firstName,
		lastName,
		email,
		dob,
		cac,
		utilityBill,
		phone,
		occupation,
		streetNum,
		street,
		city,
		state,
		country,
		idType,
		idNumber,
		postalCode,
		expiryDate,
		cacDoc,
		utilityDoc,
	} = formData;

	const { loading: cacLoading, fileUploadSuccess: cacSuccess } = useUploadFile(cac);

	const { loading: utilityLoading, fileUploadSuccess: utilitySuccess } = useUploadFile(utilityBill);

	const handleChange = (e) => {
		if (e.target.type === "tel" && isNaN(e.target.value)) {
			return;
		}
		if (e.target.type === "file") {
			setFormData((prev) => ({ ...prev, [e.target.name]: e.target.files[0] }));
		} else {
			setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
		}
	};

	const onToggleEditDetails = () => {
		if (currentAdmin?.privileges === "firstLineApproval" && currentAdmin?.role?.toLowerCase() === "finance") {
			setIsEditable(true);
			return;
		} else if (currentAdmin?.privileges?.toLowerCase() === "super administrator") {
			setIsEditable(true);
			return;
		} else {
			toast.error("You are not authorized to update these details");
			return;
		}
	};

	const handlePostalCode = () => {
		const selectedState = stateAndLga?.find((a) => a.state === state);

		if (selectedState) {
			setFormData((prev) => ({ ...prev, postalCode: selectedState?.postalCode }));
		} else {
			return;
		}
	};

	const dataToUpdate = {
		firstName,
		lastName,
		email,
		phone,
		dob,
		occupation,
		streetNum,
		street,
		city,
		state,
		country,
		postalCode,
		idType,
		idNumber,
		expiryDate,
		cacDoc: cacSuccess || cacDoc,
		utilityDoc: utilitySuccess || utilityDoc,
		gender: currentAdmin?.gender,
		address: `${streetNum}, ${street}, ${city}, ${state} ${country}`,
	};

	const handleFormSubmit = async (e) => {
		e.preventDefault();

		if (
			!firstName ||
			!lastName ||
			!email ||
			!phone ||
			!dob ||
			!occupation ||
			!streetNum ||
			!street ||
			!city ||
			!state ||
			!idNumber ||
			!expiryDate
		) {
			toast.error("Please complete the asteric fields");
			return;
		} else if (!dataToUpdate?.cacDoc || !dataToUpdate?.utilityDoc) {
			toast.error("Please upload required documents");
			return;
		}

		const objectId = companyData?.objectId;

		try {
			setSubmitLoading(true);
			const response = await createOrUpdateCompanyDetails(dataToUpdate, objectId);

			if (response) {
				dispatch(saveCompanyInsuranceInfo(response));
				toast.success("Company Information has been updated successfully");
			}
		} catch (error) {
			toast.error(error);
		} finally {
			setSubmitLoading(false);
		}
	};

	useEffect(() => {
		if (cacSuccess) {
			setFormData((prev) => ({ ...prev, cac: null }));
		}
		if (utilitySuccess) {
			setFormData((prev) => ({ ...prev, utilityBill: null }));
		} else {
			return;
		}
	}, [utilitySuccess, cacSuccess]);

	useEffect(() => {
		handlePostalCode();
	}, [state]);

	useEffect(() => {
		setFormData((prev) => ({ ...prev, ...infoToUse }));
	}, [companyData]);

	return (
		<>
			<Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={submitLoading}>
				<CircularProgress color='inherit' />
			</Backdrop>

			<FormAccordion className='mt-4 hello' title='Company Information' open={true}>
				<div>
					<div className='grid grid-cols-3 gap-5 mt-5'>
						<Formgroup type='users'>
							<label htmlFor='firstName'>
								First Name
								<Asteric />
							</label>
							<InputField
								name='firstName'
								handleChange={handleChange}
								isEditable={isEditable}
								value={firstName}
							/>
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='lastName'>
								Last Name
								<Asteric />
							</label>
							<InputField
								name='lastName'
								handleChange={handleChange}
								isEditable={isEditable}
								value={lastName}
							/>
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='email'>
								Email
								<Asteric />
							</label>
							<InputField
								name='email'
								handleChange={handleChange}
								isEditable={isEditable}
								value={email}
								type='email'
							/>
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='phone'>
								Phone
								<Asteric />
							</label>
							<InputField
								name='phone'
								handleChange={handleChange}
								isEditable={isEditable}
								value={phone}
								type='tel'
								maxLength={11}
							/>
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='dob'>
								DOB
								<Asteric />
							</label>
							<InputField
								name='dob'
								handleChange={handleChange}
								isEditable={isEditable}
								value={dob}
								type='date'
								max={formattedDate}
							/>
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='occupation'>
								Business Type
								<Asteric />
							</label>
							<SelectFieldStatus
								name='occupation'
								value={occupation}
								handleChange={handleChange}
								isEditable={isEditable}
								options={BUSINESSTYPE}
							/>
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='streetNum'>
								Street No.
								<Asteric />
							</label>
							<InputField
								name='streetNum'
								handleChange={handleChange}
								isEditable={isEditable}
								value={streetNum}
							/>
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='street'>
								Street Name
								<Asteric />
							</label>
							<InputField name='street' handleChange={handleChange} isEditable={isEditable} value={street} />
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='city'>
								City
								<Asteric />
							</label>
							<InputField name='city' handleChange={handleChange} isEditable={isEditable} value={city} />
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='city'>
								State
								<Asteric />
							</label>
							<SelectFieldStatus
								name='state'
								value={state}
								handleChange={handleChange}
								isEditable={isEditable}
								options={stateAndLga?.map((d) => d.state).sort()}
							/>
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='country'>Country</label>
							<InputField name='country' isEditable={false} value={country} />
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='country'>Postal Code</label>
							<InputField isEditable={false} value={postalCode} />
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='idType'>ID Type</label>
							<InputField name='idType' isEditable={false} value={idType} />
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='idNumber'>
								CAC Number
								<Asteric />
							</label>
							<InputField
								name='idNumber'
								isEditable={isEditable}
								value={idNumber}
								handleChange={handleChange}
							/>
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='idNumber'>
								CAC Expiry Date
								<Asteric />
							</label>
							<InputField
								name='expiryDate'
								isEditable={isEditable}
								value={expiryDate}
								handleChange={handleChange}
								type='date'
								min={formattedDate}
							/>
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='cac'>
								Upload CAC
								<Asteric />
							</label>
							<FileUpload
								isDisabled={!isEditable}
								name='cac'
								handleChange={handleChange}
								isLoading={cacLoading}
								fileInfo={cacSuccess || cacDoc}
							/>
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='utilityBill'>
								Upload Utility Bill
								<Asteric />
							</label>
							<FileUpload
								isDisabled={!isEditable}
								name='utilityBill'
								handleChange={handleChange}
								isLoading={utilityLoading}
								fileInfo={utilitySuccess || utilityDoc}
							/>
						</Formgroup>
					</div>
					<ButtonGroup>
						{isEditable ? (
							<>
								<Button color='#ffffff' backgroundcolor='#003A50' onClick={handleFormSubmit}>
									<p>Update</p>
								</Button>

								<Button
									color='#006770'
									border='true'
									backgroundcolor='#ffffff'
									onClick={() => setIsEditable(false)}>
									<p>Cancel</p>
								</Button>
							</>
						) : (
							<Button color='#fff' border='true' backgroundcolor='#003A50' onClick={onToggleEditDetails}>
								<img src={saveIcon} />
								<p>Edit Details</p>
							</Button>
						)}
					</ButtonGroup>
				</div>
			</FormAccordion>
		</>
	);
};

OtherAdminDetails.propTypes = {
	companyData: PropTypes.object,
};

export default OtherAdminDetails;

const Asteric = () => <span style={{ color: "red" }}>*</span>;
